import {Fragment, useState} from 'react'
import {InfoIcon, LinkExternalIcon} from '@primer/octicons-react'
import {AnchoredOverlay, IconButton} from '@primer/react'
import {CopyToClipboardIconButton} from '@github-ui/copy-to-clipboard/IconButton'
import {clsx} from 'clsx'

import styles from './SectionQueryPopover.module.css'
import type {SectionQuery} from './types/list-types'

const KEYWORDS = new Set(['AND', 'OR', 'NOT'])
const TOKEN_SPLIT = /(\s+|[()])/

// Lightweight tokenizer that visually mirrors the filter search bar's syntax
// highlighting (see packages/filter/input/StyledInput.tsx) for read-only display.
function HighlightedQuery({query}: {query: string}) {
  const parts = query.split(TOKEN_SPLIT)

  return (
    <>
      {parts.map((part, index) => {
        if (part === '') return null

        // Delimiters captured by TOKEN_SPLIT: whitespace runs or a single paren.
        const firstChar = part.charCodeAt(0)
        const isDelimiter = firstChar === 32 || firstChar === 9 || part === '(' || part === ')'
        const key = `${index}-${part}`
        if (isDelimiter) {
          return <Fragment key={key}>{part}</Fragment>
        }

        // Check key:value before keyword so a value like `label:OR` isn't styled as a keyword.
        const colonIndex = part.indexOf(':')
        if (colonIndex > 0 && colonIndex < part.length - 1) {
          const keyPart = part.slice(0, colonIndex)
          const valuePart = part.slice(colonIndex + 1)
          const isDynamic = valuePart.startsWith('@')
          return (
            <span key={key} className={styles.token}>
              {keyPart}:<span className={isDynamic ? styles.tokenValueDynamic : styles.tokenValue}>{valuePart}</span>
            </span>
          )
        }

        if (KEYWORDS.has(part)) {
          return (
            <span key={key} className={clsx(styles.token, styles.tokenKeyword)}>
              {part}
            </span>
          )
        }

        return (
          <span key={key} className={styles.token}>
            {part}
          </span>
        )
      })}
    </>
  )
}

export interface SectionQueryPopoverProps {
  sectionName: string
  description?: string
  queries: SectionQuery[]
  /** Base path for the "Open search" link. Defaults to `/pulls`. */
  searchHrefBase?: string
}

export function SectionQueryPopover({
  sectionName,
  description,
  queries,
  searchHrefBase = '/pulls',
}: SectionQueryPopoverProps) {
  const [open, setOpen] = useState(false)

  if (queries.length === 0) return null

  const buildSearchHref = (query: string) => `${searchHrefBase}?${new URLSearchParams({q: query}).toString()}`

  return (
    <AnchoredOverlay
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      width="large"
      focusZoneSettings={{disabled: true}}
      renderAnchor={anchorProps => (
        <IconButton
          {...anchorProps}
          className={styles.anchor}
          icon={InfoIcon}
          variant="invisible"
          size="small"
          aria-label={`About the “${sectionName}” section`}
        />
      )}
    >
      <div className={styles.content}>
        {description && <p className={styles.description}>{description}</p>}
        <ul className={styles.queryList}>
          {queries.map(q => (
            <li key={q.query} className={styles.queryItem}>
              {q.label && <div className={styles.queryLabel}>{q.label}</div>}
              <div className={styles.queryRow}>
                <code className={styles.queryText}>
                  <HighlightedQuery query={q.query} />
                </code>
                <CopyToClipboardIconButton
                  textToCopy={q.query}
                  ariaLabel={
                    q.label ? `Copy query for ${q.label} to clipboard` : `Copy query for ${sectionName} to clipboard`
                  }
                  size="small"
                />
                <IconButton
                  as="a"
                  href={buildSearchHref(q.query)}
                  icon={LinkExternalIcon}
                  variant="invisible"
                  size="small"
                  aria-label={q.label ? `Open search for ${q.label}` : `Open search for ${sectionName}`}
                />
              </div>
            </li>
          ))}
        </ul>
      </div>
    </AnchoredOverlay>
  )
}
